import { useMutation } from '@apollo/client';
import type {
  DeleteQuestionnaireTemplateMutation,
  DeleteQuestionnaireTemplateMutationVariables,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { DeleteQuestionnaireTemplateDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useDeleteQuestionnaireTemplateTRPC } from './useDeleteQuestionnaireTemplateTRPC';

export const useDeleteQuestionnaireTemplate = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation (legacy)
  const [deleteQuestionnaireTemplateGraphQL, graphqlState] = useMutation(
    DeleteQuestionnaireTemplateDocument,
    {
      update: (cache) => {
        evictField(cache, 'questionnaire_template');
        evictField(cache, 'questionnaire_template_by_pk');
      },
    }
  );

  // tRPC mutation
  const trpcMutation = useDeleteQuestionnaireTemplateTRPC();

  const deleteQuestionnaireTemplate = async (input: {
    variables: DeleteQuestionnaireTemplateMutationVariables;
  }): Promise<DeleteQuestionnaireTemplateMutation> => {
    if (trpcEnabled) {
      return trpcMutation.deleteQuestionnaireTemplate(input.variables.Id);
    }

    const result = await deleteQuestionnaireTemplateGraphQL({
      variables: input.variables,
    });
    if (!result.data) {
      throw new Error('Failed to delete questionnaire template');
    }

    return result.data;
  };

  return {
    deleteQuestionnaireTemplate,
    loading: trpcEnabled ? trpcMutation.loading : graphqlState.loading,
    error: trpcEnabled ? trpcMutation.error : graphqlState.error,
  };
};
